import { useIsRibbonSidebarEnabled } from '@/app-shell/flags/agents-flags';
import { useIsChronicleDevDashboardEnabled } from '@/app-shell/flags/desktop-flags';
import { useIsRouteActive } from '@/app-shell/layout/Navigation/hooks/useIsRouteActive';
import { useMobilePanel } from '@/app-shell/layout/Navigation/mobile';
import { NavItem } from '@/app-shell/layout/Navigation/sidebar/nav-item';
import { useSettingsModal } from '@/app-shell/settings/hooks/useSettingsModal';
import { isTauri } from '@/tauri/utils';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { SidebarMenu } from '@heidi/design-system/page-shell';
import { Building, Computer, Radio, Settings } from 'lucide-react';
import { defineMessages, useIntl } from 'react-intl';

const messages = defineMessages({
  organization: {
    defaultMessage: 'Organization',
    id: 'tzUV0Rkhfw',
    description: 'Sidebar nav entry for global admins to open organization settings',
  },
  team: {
    defaultMessage: 'Team',
    id: 'JimB8vfrxJ',
    description: 'Sidebar nav entry linking to team settings',
  },
  settings: {
    defaultMessage: 'Settings',
    id: 'Ub8IhoW/oY',
    description: 'Sidebar nav entry that opens the settings modal',
  },
  appInfo: {
    defaultMessage: 'App Info',
    id: 'AVmXNolNzb',
    description: 'Sidebar nav entry linking to desktop app info, non-production only',
  },
  chronicleDev: {
    defaultMessage: 'Heidi Remote (Dev)',
    id: '5LNzSuOU8E',
    description: 'Sidebar nav entry linking to the Chronicle dev dashboard',
  },
});

export interface SettingsGroupNavItemsProps {
  startTransition: (fn: () => void) => void;
  closeSidePanels: () => void;
  isGlobalAdmin: boolean;
  isMobile: boolean;
}

export function SettingsGroupNavItems({
  startTransition,
  closeSidePanels,
  isGlobalAdmin,
}: SettingsGroupNavItemsProps) {
  const intl = useIntl();
  const { dismissMobileSidebar } = useMobilePanel();
  const isRouteActive = useIsRouteActive();
  const isChronicleDevDashboardEnabled = useIsChronicleDevDashboardEnabled();
  const isRibbonSidebarEnabled = useIsRibbonSidebarEnabled();
  const { open: openSettingsModal } = useSettingsModal();

  const handleNavClick = (extra?: () => void) => {
    startTransition(() => {
      closeSidePanels();
      extra?.();
    });
    dismissMobileSidebar();
  };

  return (
    <SidebarMenu>
      {!isRibbonSidebarEnabled && (
        <>
          {isGlobalAdmin ? (
            <NavItem
              label={intl.formatMessage(messages.organization)}
              icon={<Building />}
              href="/organization"
              isActive={isRouteActive('/organization')}
              onClick={() => handleNavClick()}
              testId="sidebar-link-organization"
            />
          ) : (
            <NavItem
              label={intl.formatMessage(messages.team)}
              icon={<Building />}
              href="/team"
              isActive={isRouteActive('/team')}
              onClick={() => handleNavClick()}
              testId="sidebar-link-team"
            />
          )}
        </>
      )}
      {!isRibbonSidebarEnabled && (
        <NavItem
          label={intl.formatMessage(messages.settings)}
          icon={<Settings />}
          isActive={false}
          onClick={() => handleNavClick(() => openSettingsModal(undefined, 'sidebar'))}
          testId="sidebar-link-settings"
        />
      )}
      {isTauri() && getEnvironment() !== ENV_NAME.PROD && (
        <NavItem
          label={intl.formatMessage(messages.appInfo)}
          icon={<Computer />}
          href="/tauri-settings"
          isActive={isRouteActive('/tauri-settings')}
          onClick={() => handleNavClick()}
        />
      )}
      {isChronicleDevDashboardEnabled && (
        <NavItem
          label={intl.formatMessage(messages.chronicleDev)}
          icon={<Radio />}
          href="/dev/chronicle"
          isActive={isRouteActive('/dev/chronicle')}
          onClick={() => handleNavClick()}
        />
      )}
    </SidebarMenu>
  );
}
